import { Box, Button, Checkbox, Chip, Container, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme, Divider, TextField, Paper, Card, CardContent, Tab, Tabs, AppBar, Toolbar, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, CssBaseline } from "@mui/material";
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HelpIcon from "@mui/icons-material/HelpOutline";

import Header from "../../../Layout/Header";
import CallAlertBar from "../../../Layout/Alertbar";
import QRCodeScanner from "./QRCodeScanner";
import { callApi } from "../../../utils/apiClient";
import { Production_API_ENDPOINTS } from "../../../config/api/apiCallProduction";
import { tokens } from "../../../theme/theme";
import { hasPermission } from "../../../components/user-permission/AuthorizationParameter";
import useUserPermissions from "../../../components/user-permission/UserPermission";

const ProductScanningPage = ({ handleBack }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const userPermissions = useUserPermissions();

  // State management
  const [scannedItems, setScannedItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [qrInput, setQrInput] = useState("");
  const [useFallbackMode, setUseFallbackMode] = useState(false); // Default to camera (try first)
  const [inputTab, setInputTab] = useState(0); // 0: Manual, 1: Upload Image
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImagePreview, setUploadedImagePreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (option) => {
    if (option === 'Clear All') {
      setScannedItems([]);
      setSelectedIds(new Set());
      setSelectAll(false);
    } else if (option === 'Refresh Page') {
      window.location.reload();
    }
    handleMenuClose();
  };

  // Alert state - following the CallAlertBar pattern
  const [qrScanMsgOpen, setQrScanMsgOpen] = useState(false);
  const [qrScanSeverityvalue, setQrScanSeverityvalue] = useState('info');
  const [qrScanMessageValue, setQrScanMessageValue] = useState('');
  const [qrScanAlertTitleValue, setQrScanAlertTitleValue] = useState('');

  // Show alert
  const showAlert = (severity, title, message) => {
    setQrScanSeverityvalue(severity);
    setQrScanAlertTitleValue(title);
    setQrScanMessageValue(message);
    setQrScanMsgOpen(true);
  };

  /**
   * Handle successful QR scan - Call validation API
   * @param {string} qrCode - The decoded QR code value
   * @param {string} stage - The current scanning stage
   */
  const handleQRScanSuccess = (qrCode, stage) => {
    setValidating(true);

    // Call validation API
    console.log("[ProductScanningPage] Validating QR code:", qrCode);

    callApi(
      Production_API_ENDPOINTS.qrScanning.validate,
      "POST",
      { "Content-Type": "application/json" },
      {
        qrCode: qrCode,
        scannedBy: localStorage.getItem("userID") || "Unknown"
      }
    )
      .then((response) => {
        console.log("[ProductScanningPage] Validation response:", response);

        if (response && response.isValid) {
          // QR code is valid - add to grid with product type
          // Check for duplicate against latest state (inside .then)
          setScannedItems(prev => {
            if (prev.some(item => item.itemNo === (response.itemNo || qrCode))) {
              showAlert("warning", "Duplicate Item", `QR code already scanned: ${response.itemNo || qrCode}`);
              return prev; // no change
            }

            const newItem = {
              itemNo: response.itemNo || qrCode,
              productType: response.productType || "Unknown",
              scannedDate: new Date().toLocaleDateString(),
              scannedTime: new Date().toLocaleTimeString(),
              scannedBy: localStorage.getItem("userID") || "Unknown",
              status: "Pending Save",
              stage: stage,
              timestamp: new Date().getTime()
            };

            showAlert("success", "✅ Valid QR Code", `${response.itemNo} (${response.productType}) added to grid`);
            return [...prev, newItem];
          });
        } else {
          // QR code not found
          showAlert("warning", "Invalid QR Code", response?.message || "QR code not found in system");
        }
      })
      .catch((error) => {
        console.error("[ProductScanningPage] Validation API Error:", error);
        showAlert("warning", "Validation Error", error?.message || "Failed to validate QR code");
      })
      .finally(() => {
        setValidating(false);
      });
  };

  /**
   * Handle scan error
   * @param {Error} error - The error that occurred
   * @param {string} stage - The current scanning stage
   */
  const handleQRScanError = (error, stage) => {
    console.error(`[${stage}] QR Scan Error:`, error);
    console.error("[ProductScanningPage] Error details:", {
      name: error?.name,
      message: error?.message,
      toString: error?.toString?.()
    });

    // Show error alert to user instead of silently switching to fallback
    if (error?.message?.includes("NotAllowedError")) {
      showAlert("warning", "Camera Permission Denied", "Please allow camera access in your browser settings and refresh the page.");
    } else if (error?.message?.includes("NotFoundError")) {
      showAlert("warning", "No Camera Found", "No camera device detected. Please check your hardware or use the fallback mode.");
    } else if (error?.message?.includes("NotReadableError")) {
      showAlert("warning", "Camera In Use", "Camera is being used by another application. Please close it and try again.");
    } else if (error?.message?.includes("SecurityError")) {
      showAlert("warning", "Security Error", "Camera access blocked. Use HTTPS or localhost only.");
    }
  };

  /**
   * Handle manual QR input (fallback for no camera)
   */
  const handleManualQRInput = (e) => {
    if (validating) return;
    if (e.key === "Enter" && qrInput.trim()) {
      e.preventDefault();
      handleQRScanSuccess(qrInput.trim(), "validation");
      setQrInput("");
    }
  };

  /**
   * Handle image file upload
   */
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      showAlert("warning", "Invalid File", "Please select an image file");
      return;
    }

    // Set preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target?.result;
      setUploadedImagePreview(imageData);
    };
    reader.readAsDataURL(file);
    setUploadedImage(file);
  };

  /**
   * Scan QR code from uploaded image using jsQR library
   */
  const handleScanFromImage = async () => {
    if (!uploadedImage || !uploadedImagePreview) {
      showAlert("warning", "No Image", "Please upload an image first");
      return;
    }

    setUploadLoading(true);
    try {
      const jsQR = require("jsqr");

      // Load image and create canvas
      const img = new Image();
      img.onload = async () => {
        try {
          // Create canvas from image with optimized size
          const maxSize = 1000; // Optimal size for QR detection
          let width = img.width;
          let height = img.height;

          // Scale down large images for better QR detection
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            showAlert("warning", "Error", "Could not process image");
            setUploadLoading(false);
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Get image data
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

          // Scan for QR code with improved detection
          const code = jsQR(imageData.data, imageData.width, imageData.height);

          if (code) {
            handleQRScanSuccess(code.data, "validation");
            showAlert("success", "✅ QR Code Found", `Scanned: ${code.data}`);
          } else {
            showAlert(
              "warning",
              "No QR Code Detected",
              "Could not find a QR code in the image. Make sure it's clear and visible."
            );
          }
        } catch (error) {
          console.error("Canvas processing error:", error);
          showAlert("warning", "Error", "Failed to process image");
        } finally {
          setUploadLoading(false);
        }
      };

      img.onerror = () => {
        console.error("Image load error");
        showAlert("warning", "Error", "Failed to load image");
        setUploadLoading(false);
      };

      // Load image from preview
      img.src = uploadedImagePreview;
    } catch (error) {
      console.error("Image scan error:", error);
      showAlert("warning", "Error", "Failed to scan image: " + error.message);
      setUploadLoading(false);
    }
  };

  /**
   * Clear uploaded image
   */
  const handleClearImage = () => {
    setUploadedImage(null);
    setUploadedImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(scannedItems.map(item => item.itemNo));
      setSelectedIds(allIds);
      setSelectAll(true);
    } else {
      setSelectedIds(new Set());
      setSelectAll(false);
    }
  };

  // Handle individual row checkbox
  const handleRowSelect = (itemNo) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(itemNo)) {
      newSelected.delete(itemNo);
    } else {
      newSelected.add(itemNo);
    }
    setSelectedIds(newSelected);
    setSelectAll(newSelected.size === scannedItems.length && scannedItems.length > 0);
  };

  // Remove selected items
  const handleRemoveSelected = () => {
    if (selectedIds.size === 0) {
      showAlert("warning", "No Selection", "Please select items to remove");
      return;
    }
    const filtered = scannedItems.filter(item => !selectedIds.has(item.itemNo));
    setScannedItems(filtered);
    setSelectedIds(new Set());
    setSelectAll(false);
    showAlert("info", "Items Removed", `${selectedIds.size} item(s) removed from grid`);
  };

  // Clear all items
  const handleClearAll = () => {
    setScannedItems([]);
    setSelectedIds(new Set());
    setSelectAll(false);
    showAlert("info", "Cleared", "All items cleared from grid");
  };

  // Save selected items
  const handleSaveSelected = () => {
    if (selectedIds.size === 0) {
      showAlert("warning", "No Selection", "Please select at least one item to save");
      return;
    }

    setSaving(true);
    const itemsToSave = scannedItems
      .filter(item => selectedIds.has(item.itemNo))
      .map(item => ({
        itemNo: item.itemNo,
        productType: item.productType
      }));

    callApi(
      Production_API_ENDPOINTS.qrScanning.save,
      "POST",
      { "Content-Type": "application/json" },
      {
        scannedBy: localStorage.getItem("userID") || "Unknown",
        scanDateTime: new Date().toISOString(),
        items: itemsToSave
      }
    )
      .then((response) => {
        if (response && response.success) {
          showAlert("success", "Saved Successfully", `${selectedIds.size} item(s) saved to database`);
          // Remove saved items from grid
          const remaining = scannedItems.filter(item => !selectedIds.has(item.itemNo));
          setScannedItems(remaining);
          setSelectedIds(new Set());
          setSelectAll(false);
        } else {
          showAlert("warning", "Save Failed", response?.message || "Failed to save items");
        }
      })
      .catch((error) => {
        console.error("Save API Error:", error);
        showAlert("warning", "Save Error", error?.message || "Failed to save items to database");
        // Do NOT clear grid on error
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Box sx={{ backgroundColor: colors.primary[500], minHeight: "100vh", pb: 3 }}>
      {/* Professional Header Bar */}
      <CssBaseline />
      <AppBar position="static" sx={{ backgroundColor: "inherit", borderRadius: "0px", height: "48px", justifyContent: "center" }}>
        <Toolbar>
          <Button
            color="secondary"
            sx={{ textTransform: 'none', fontSize: "16px", fontWeight: "500" }}
          >
            {t("prod_menu.qrcodescanning") || "QR Code Scanning"}
          </Button>

          {/* More Options Menu */}
          <IconButton
            size="small"
            sx={{ marginLeft: 'auto' }}
            onClick={handleMenuClick}
          >
            <MoreVertIcon sx={{ fontSize: "18px" }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => handleMenuItemClick('Clear All')}>
              <ListItemIcon>
                <ClearIcon />
              </ListItemIcon>
              <ListItemText primary="Clear All Items" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleMenuItemClick('Refresh Page')}>
              <ListItemIcon>
                <RefreshIcon />
              </ListItemIcon>
              <ListItemText primary="Refresh Page" />
            </MenuItem>
          </Menu>

          <IconButton
            size="small"
            sx={{ mr: 1, ml: 1 }}
          >
            <HelpIcon sx={{ fontSize: "18px" }} />
          </IconButton>

          <IconButton onClick={handleBack}>
            <ArrowBackIcon fontSize="inherit" style={{ fontSize: "18px", color: "#2196F3" }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Alert Bar - Below AppBar for Better UX */}
      <Box sx={{ backgroundColor: colors.primary[500] }}>
        <CallAlertBar
          parentRollMsgOpen={qrScanMsgOpen}
          setParentRollMsgOpen={setQrScanMsgOpen}
          parentRollSeverityvalue={qrScanSeverityvalue}
          setParentRollSeverityvalue={setQrScanSeverityvalue}
          parentRollAlertTitleValue={qrScanAlertTitleValue}
          setParentRollAlertTitleValue={setQrScanAlertTitleValue}
          parentRollMessageValue={qrScanMessageValue}
          setParentRollMessageValue={setQrScanMessageValue}
        />
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* QR Code Scanner Component OR Manual Input Fallback */}
        <Box mb={4}>
          {!useFallbackMode ? (
            <Box>
              <QRCodeScanner
                ref={scannerRef}
                stage="validation"
                onScanSuccess={handleQRScanSuccess}
                onScanError={handleQRScanError}
                stopOnSuccess={true}
                autoStart={false}
                facingMode="environment"
                fps={10}
                qrCodeSuccessMessage="QR Code detected. Validating..."
              />
              <Box mt={2} textAlign="center">
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => setUseFallbackMode(true)}
                  size="small"
                >
                  📝 Switch to Manual Input / Image Upload
                </Button>
                <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                  Having camera issues? Check browser console (F12) for detailed error messages.
                </Typography>
              </Box>
            </Box>
          ) : (
            // Fallback: Manual QR Input + Image Upload (for systems without camera)
            <Card sx={{
              backgroundColor: colors.primary[400],
              boxShadow: theme.shadows[3],
              border: `2px solid ${colors.redAccent[700]}`,
              transition: "all 0.3s ease"
            }}>
              <CardContent>
                <Typography variant="h6" color={colors.redAccent[400]} mb={2}>
                  ⚠️ Camera Not Available
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  Your system doesn't have a camera. Choose below to enter QR codes manually or upload an image.
                </Typography>

                {/* Tabs for Manual Input vs Image Upload */}
                <Tabs
                  value={inputTab}
                  onChange={(e, newValue) => setInputTab(newValue)}
                  sx={{
                    mb: 3,
                    borderBottom: `1px solid ${colors.primary[300]}`,
                    "& .MuiTab-root": {
                      color: colors.grey[400],
                      "&.Mui-selected": {
                        color: colors.greenAccent[400]
                      }
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: colors.greenAccent[400]
                    }
                  }}
                >
                  <Tab label="📝 Manual Input" icon={<Typography>Type QR Code</Typography>} />
                  <Tab label="🖼️ Upload Image" icon={<ImageIcon />} />
                </Tabs>

                {/* Tab 1: Manual Input */}
                {inputTab === 0 && (
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Enter QR Code or Item No"
                      placeholder="e.g., BND-10025 or paste QR value"
                      value={qrInput}
                   
                      onChange={(e) => setQrInput(e.target.value)}
                      onKeyDown={handleManualQRInput}
                      disabled={validating}
                      helperText="Press Enter to add item"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: colors.primary[500],
                          color: colors.grey[100],
                          fontSize: "16px"
                        },
                        "& .MuiOutlinedInput-input": {
                          color: colors.grey[100]
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.primary[300]
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: colors.grey[400],
                          opacity: 0.7
                        }
                      }}
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 2 }}>
                      💡 Example: BND-10025, RDL-50045, PAL-30012
                    </Typography>
                  </Box>
                )}

                {/* Tab 2: Image Upload */}
                {inputTab === 1 && (
                  <Box>
                    {/* File Input (Hidden) */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: "none" }}
                    />

                    {/* Upload Button */}
                    {!uploadedImagePreview ? (
                      <Box
                        sx={{
                          border: `2px dashed ${colors.primary[300]}`,
                          borderRadius: 2,
                          p: 3,
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          "&:hover": {
                            backgroundColor: colors.primary[500],
                            borderColor: colors.greenAccent[400]
                          }
                        }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <CloudUploadIcon sx={{ fontSize: 48, color: colors.greenAccent[400], mb: 1 }} />
                        <Typography variant="h6" mb={1}>
                          Click to Upload QR Code Image
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          or drag and drop an image file here
                        </Typography>
                      </Box>
                    ) : (
                      // Image Preview
                      <Box>
                        <Box
                          sx={{
                            backgroundColor: colors.primary[500],
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            textAlign: "center"
                          }}
                        >
                          <img
                            src={uploadedImagePreview}
                            alt="QR Code Preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "300px",
                              borderRadius: "8px"
                            }}
                          />
                        </Box>

                        {/* Action Buttons */}
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="success"
                              onClick={handleScanFromImage}
                              disabled={uploadLoading}
                            >
                              {uploadLoading ? "Scanning..." : "Scan QR Code"}
                            </Button>
                          </Grid>
                          <Grid item xs={6}>
                            <Button
                              fullWidth
                              variant="outlined"
                              color="error"
                              onClick={handleClearImage}
                            >
                              Clear
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 2 }}>
                      ✅ Supported formats: PNG, JPG, JPEG, GIF, WebP
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Action Buttons Row */}
        <Box
          sx={{
            backgroundColor: colors.primary[400],
            p: 1.5,
            borderRadius: 2,
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            boxShadow: theme.shadows[2],
            border: `1px solid ${colors.primary[300]}`,
            transition: "all 0.3s ease"
          }}
        >
          <Checkbox
            checked={selectAll}
            onChange={handleSelectAll}
            disabled={scannedItems.length === 0}
            label="Select All"
            color="info"
          />
          <Typography sx={{ minWidth: "100px", color: colors.grey[100] }}>
            Selected: {selectedIds.size} / {scannedItems.length}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
            onClick={handleRemoveSelected}
            disabled={selectedIds.size === 0}
            color="warning"
            size="small"
            sx={{
              transition: "all 0.2s ease",
              "&:hover:not(:disabled)": {
                backgroundColor: "rgba(255, 193, 7, 0.1)"
              }
            }}
          >
            Remove Selected
          </Button>
          {hasPermission(userPermissions, "QR Code Scanning", "Create") && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSelected}
              disabled={selectedIds.size === 0 || saving}
              color="success"
              size="small"
              sx={{
                fontWeight: "bold",
                transition: "all 0.3s ease",
                "&:hover:not(:disabled)": {
                  transform: "scale(1.05)",
                  boxShadow: theme.shadows[4]
                },
                "&:disabled": {
                  opacity: 0.6
                }
              }}
            >
              {saving ? "Saving..." : "Save Selected"}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearAll}
            disabled={scannedItems.length === 0}
            color="error"
            size="small"
            sx={{
              transition: "all 0.2s ease",
              "&:hover:not(:disabled)": {
                backgroundColor: "rgba(211, 47, 47, 0.1)"
              }
            }}
          >
            Clear All
          </Button>
        </Box>

        {/* Data Table */}
        <Box
          sx={{
            backgroundColor: colors.primary[400],
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: theme.shadows[3],
            border: `1px solid ${colors.primary[300]}`,
            transition: "all 0.3s ease"
          }}
        >
          <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.primary[500] }}>
                    <TableCell
                      sx={{
                        color: colors.grey[100],
                        fontWeight: "bold",
                        width: "40px",
                        py: 1,
                        px: 0.5,
                        fontSize: "0.85rem"
                      }}
                    >
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                        disabled={scannedItems.length === 0}
                        color="info"
                        size="small"
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: colors.grey[100],
                        fontWeight: "bold",
                        width: "40px",
                        py: 1,
                        px: 0.5,
                        fontSize: "0.85rem"
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: "bold", py: 1, px: 1, fontSize: "0.85rem" }}>
                      Item No
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: "bold", py: 1, px: 1, fontSize: "0.85rem" }}>
                      Product Type
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: "bold", py: 1, px: 1, fontSize: "0.85rem" }}>
                      Scanned Date
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: "bold", py: 1, px: 1, fontSize: "0.85rem" }}>
                      Scanned Time
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: "bold", py: 1, px: 1, fontSize: "0.85rem" }}>
                      Scanned By
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: "bold", py: 1, px: 1, fontSize: "0.85rem" }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scannedItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3, color: colors.grey[300] }}>
                        <Typography color="inherit">
                          No items scanned yet. Scan a QR code to begin.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    scannedItems.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor:
                            index % 2 === 0
                              ? colors.primary[500]
                              : colors.primary[400],
                          "&:hover": {
                            backgroundColor: colors.primary[300],
                            boxShadow: `inset 0 0 8px ${colors.greenAccent[900]}`,
                            transition: "all 0.2s ease"
                          },
                          transition: "all 0.2s ease",
                          cursor: "pointer"
                        }}
                      >
                        <TableCell align="center" sx={{ width: "40px", color: colors.grey[100], py: 1, px: 0.5 }}>
                          <Checkbox
                            checked={selectedIds.has(item.itemNo)}
                            onChange={() => handleRowSelect(item.itemNo)}
                            color="info"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ width: "40px", fontWeight: "bold", color: colors.grey[100], py: 1, px: 0.5, fontSize: "0.85rem" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ color: colors.grey[100], py: 1, px: 1, fontSize: "0.85rem" }}>{item.itemNo}</TableCell>
                        <TableCell sx={{ color: colors.grey[100], py: 1, px: 1, fontSize: "0.85rem" }}>{item.productType}</TableCell>
                        <TableCell sx={{ color: colors.grey[100], py: 1, px: 1, fontSize: "0.85rem" }}>{item.scannedDate}</TableCell>
                        <TableCell sx={{ color: colors.grey[100], py: 1, px: 1, fontSize: "0.85rem" }}>{item.scannedTime}</TableCell>
                        <TableCell sx={{ color: colors.grey[100], py: 1, px: 1, fontSize: "0.85rem" }}>{item.scannedBy}</TableCell>
                        <TableCell sx={{ color: colors.grey[100], py: 1, px: 1 }}>
                          <Chip
                            label={item.status}
                            color="warning"
                            size="small"
                            variant="outlined"
                            sx={{ color: colors.orangeAccent[400], fontSize: "0.75rem", height: "24px" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        </Box>
      </Container>
    </Box>
  );
};

export default ProductScanningPage;
